import { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faFilter, faSearch, faXmark, faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { usePositions, useStaticData, useBaseData, useUnlocodeRegister, useCountries } from '../hooks/useVessels';
import { useFavorites } from '../hooks/useFavorites';
import { Spinner } from '../components/Spinner';
import { getVesselName, timeSince, formatEta } from '../utils/vessel';
import { translateDestination } from '../utils/destination';
import type { VesselPosition, StaticVesselData, BaseVesselData, UnlocodeEntry, Country } from '../types/vessel';

interface VesselRowProps {
  id: string;
  vessel: VesselPosition;
  stat?: StaticVesselData;
  base?: BaseVesselData;
  unlocodeRegister: UnlocodeEntry[];
  countries: Country[];
  isFavorite: boolean;
  onToggleFavorite: () => void;
}

const VesselRow = memo(function VesselRow({ id, vessel, stat, base, unlocodeRegister, countries, isFavorite, onToggleFavorite }: VesselRowProps) {
  const [optimisticFavorite, setOptimisticFavorite] = useState(isFavorite);

  useEffect(() => {
    setOptimisticFavorite(isFavorite);
  }, [isFavorite]);

  const handleToggleFavorite = () => {
    setOptimisticFavorite(!optimisticFavorite);
    onToggleFavorite();
  };

  const name = getVesselName(vessel, id, base, stat);
  const destination = translateDestination(vessel.Destination, unlocodeRegister, countries);
  const tank = base?.LNGtankCapacity && String(base.LNGtankCapacity).toUpperCase() !== 'NULL' ? `${base.LNGtankCapacity}m³` : '-';

  return (
    <tr 
      style={{ cursor: 'pointer' }} 
      className="table-row"
    >
      <td style={{ padding: '10px 8px', borderBottom: '1px solid #eee', fontSize: '11px', textAlign: 'center' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleFavorite();
          }}
          style={{
            background: 'none',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            color: optimisticFavorite ? '#f59e0b' : '#ccc',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={optimisticFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <FontAwesomeIcon icon={optimisticFavorite ? faStarSolid : faStarRegular} style={{ fontSize: '14px' }} />
        </button>
      </td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stat?.RegisteredOwner || 'N/A'}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{vessel.ReceivedDate ? timeSince(new Date(vessel.ReceivedDate)) : 'N/A'}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{vessel.SoG?.toFixed(1) || '-'} kn</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{vessel.Status || '-'}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{destination}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{formatEta(vessel.ETA)}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{stat?.ShipTypeLevel5 || base?.Type || 'N/A'}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{tank}</td>
      <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontSize: '11px' }}>{vessel.IMO || id}</td>
    </tr>
  );
});

const ITEMS_PER_PAGE = 50;

export function TableView() {
  const navigate = useNavigate();
  const { data: vessels = {}, isLoading: vesselsLoading, isError } = usePositions();
  const { data: staticData = {}, isLoading: staticLoading } = useStaticData();
  const { data: baseData = {}, isLoading: baseLoading } = useBaseData();
  const { data: unlocodeRegister = [] } = useUnlocodeRegister();
  const { data: countries = [] } = useCountries();
  const { favorites, toggleFavorite } = useFavorites();

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [isTableReady, setIsTableReady] = useState(false);
  const [filters, setFilters] = useState({
    type: '', owner: '', tankMin: '', tankMax: '', destination: '', etaMin: '', etaMax: '', status: '',
  });

  const isLoading = vesselsLoading || staticLoading || baseLoading;

  const handleToggleFavorite = useCallback((id: string) => {
    const vessel = vessels[id];
    if (vessel) toggleFavorite(id, vessel, baseData[id], staticData[id]);
  }, [vessels, baseData, staticData, toggleFavorite]);

  // Delay table rendering to show loading state
  useEffect(() => {
    if (!isLoading && Object.keys(vessels).length > 0) {
      const timer = setTimeout(() => setIsTableReady(true), 100);
      return () => clearTimeout(timer);
    }
    setIsTableReady(false);
  }, [isLoading, vessels]);

  const uniqueTypes = useMemo(() => {
    const types = new Set<string>();
    Object.keys(staticData).forEach((id) => {
      if (staticData[id]?.ShipTypeLevel5) types.add(staticData[id].ShipTypeLevel5!);
    });
    return Array.from(types).sort();
  }, [staticData]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    Object.values(vessels).forEach((v) => { if (v.Status) statuses.add(v.Status); });
    return Array.from(statuses).sort();
  }, [vessels]);

  const filteredVessels = useMemo(() => {
    return Object.entries(vessels).filter(([id, vessel]) => {
      const name = getVesselName(vessel, id, baseData[id], staticData[id]).toLowerCase();
      const stat = staticData[id];
      const base = baseData[id];

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!name.includes(q) && !id.includes(q)) return false;
      }
      if (filters.type && stat?.ShipTypeLevel5 !== filters.type) return false;
      if (filters.owner && !stat?.RegisteredOwner?.toLowerCase().includes(filters.owner.toLowerCase())) return false;

      const tank = base?.LNGtankCapacity || 0;
      if (filters.tankMin && tank < parseInt(filters.tankMin)) return false;
      if (filters.tankMax && tank > parseInt(filters.tankMax)) return false;
      if (filters.destination && !vessel.Destination?.toLowerCase().includes(filters.destination.toLowerCase())) return false;

      if (vessel.ETA && !vessel.ETA.startsWith('9999')) {
        const etaDays = Math.ceil((new Date(vessel.ETA).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        if (filters.etaMin && etaDays < parseInt(filters.etaMin)) return false;
        if (filters.etaMax && etaDays > parseInt(filters.etaMax)) return false;
      }
      if (filters.status && vessel.Status !== filters.status) return false;
      return true;
    });
  }, [vessels, staticData, baseData, searchQuery, filters]);

  const displayedVessels = useMemo(() => {
    return filteredVessels.slice(0, displayCount);
  }, [filteredVessels, displayCount]);

  const clearFilters = useCallback(() => {
    setFilters({ type: '', owner: '', tankMin: '', tankMax: '', destination: '', etaMin: '', etaMax: '', status: '' });
    setSearchQuery('');
    setDisplayCount(ITEMS_PER_PAGE);
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && displayCount < filteredVessels.length) {
      setDisplayCount(prev => Math.min(prev + ITEMS_PER_PAGE, filteredVessels.length));
    }
  }, [displayCount, filteredVessels.length]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setDisplayCount(ITEMS_PER_PAGE);
  }, []);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setDisplayCount(ITEMS_PER_PAGE);
  }, []);

  if (isLoading) {
    return <Spinner message="Loading vessel data..." />;
  }
  
  if (isError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', background: 'white' }}>
        <p>Error loading data</p>
        <button onClick={() => navigate('/')} style={{ marginTop: '16px', padding: '8px 16px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          Back to Map
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'white' }}>
      <style>{`
        .table-row:hover { background: rgba(82,83,144,0.05) !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      
      {/* Header */}
      <header style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
        <button
          style={{ width: '36px', height: '36px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => navigate('/')}
        >
          <FontAwesomeIcon icon={faMap} />
        </button>
        <button
          style={{ width: '36px', height: '36px', background: showFilters ? '#424279' : '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} />
        </button>
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px', marginLeft: 'auto' }}>
          <input
            type="text"
            placeholder="Search vessels..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ width: '100%', padding: '10px 16px 10px 40px', border: '1px solid #ddd', borderRadius: '24px', fontSize: '14px', outline: 'none' }}
          />
          <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#525390' }} />
        </div>
        <span style={{ fontSize: '12px', color: '#666' }}>
          {filteredVessels.length} vessels
        </span>
      </header>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Filters Sidebar */}
        {showFilters && (
          <aside style={{ width: '280px', background: '#f8f9fa', borderRight: '1px solid #e0e0e0', padding: '16px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#525390', fontSize: '16px', fontWeight: 600 }}>Filters</h3>
              <button onClick={clearFilters} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FontAwesomeIcon icon={faXmark} /> Clear all
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Ship Type</label>
                <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="">All Types</option>
                  {uniqueTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Owner</label>
                <input type="text" placeholder="Search owner..." value={filters.owner} onChange={(e) => handleFilterChange('owner', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Destination</label>
                <input type="text" placeholder="Search destination..." value={filters.destination} onChange={(e) => handleFilterChange('destination', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px' }}>Status</label>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="">All Statuses</option>
                  {uniqueStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </aside>
        )}

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }} onScroll={handleScroll}>
          {!isTableReady && (
            <div style={{ 
              position: 'absolute', 
              inset: 0, 
              background: 'rgba(255,255,255,0.9)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              zIndex: 20
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '3px solid #e0e0e0', 
                  borderTopColor: '#525390', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 12px'
                }} />
                <p style={{ color: '#666', fontSize: '14px' }}>Preparing table...</p>
              </div>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#525390', color: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', fontWeight: 600, width: '40px' }}>★</th>
                {['Ship name', 'Owner', 'Last updated', 'Speed', 'Status', 'Destination', 'ETA', 'Ship type', 'Tank Capacity', 'IMO'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isTableReady && displayedVessels.map(([id, vessel]) => (
                <VesselRow
                  key={id}
                  id={id}
                  vessel={vessel}
                  stat={staticData[id]}
                  base={baseData[id]}
                  unlocodeRegister={unlocodeRegister}
                  countries={countries}
                  isFavorite={!!favorites[id]}
                  onToggleFavorite={() => handleToggleFavorite(id)}
                />
              ))}
            </tbody>
          </table>
          {displayedVessels.length < filteredVessels.length && (
            <div style={{ textAlign: 'center', padding: '16px', color: '#666', fontSize: '13px' }}>
              Showing {displayedVessels.length} of {filteredVessels.length} vessels. Scroll for more...
            </div>
          )}
          {filteredVessels.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>No vessels match your criteria</div>
          )}
        </div>
      </div>
    </div>
  );
}
